import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Checkbox, ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { serverFieldErrors } from '@/components/settings-section';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { ROLE_LABELS, useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { ltr } from '@/lib/arabic';
import { useT } from '@/lib/i18n';

export const Route = createFileRoute('/settings/users')({ component: ManageUsers });

interface Membership {
  userId: string;
  email: string;
  name: string;
  role: keyof typeof ROLE_LABELS;
  closeRights: boolean;
  hasSignedIn: boolean;
  createdAt: string;
}

const STAFF = new Set(['outss_admin', 'outss_accountant']);
const ROLE_KEYS = ['client_owner', 'client_viewer', 'outss_accountant', 'outss_admin'] as const;
type RoleKey = (typeof ROLE_KEYS)[number];
const isRoleKey = (r: string): r is RoleKey => (ROLE_KEYS as readonly string[]).includes(r);

function useMemberships() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['memberships', orgId],
    queryFn: () => api<Membership[]>(`/orgs/${orgId}/memberships`),
    enabled: Boolean(orgId),
  });
}

/** Screen G2 (document 06): the org's users, client users first, plus assigned Outss staff. */
function ManageUsers() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const isAdmin = me.data?.roles.includes('outss_admin') ?? false;
  const list = useMemberships();
  const [editing, setEditing] = useState<{ open: boolean; member: Membership | null }>({
    open: false,
    member: null,
  });
  const [removing, setRemoving] = useState<Membership | null>(null);

  const clients = (list.data ?? []).filter((m) => !STAFF.has(m.role));
  const staff = (list.data ?? []).filter((m) => STAFF.has(m.role));

  const table = (rows: Membership[], heading: string) => (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-4 py-2 text-sm font-semibold">{heading}</div>
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">
          {t('settingsPages.general.users.noUsers')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('settingsPages.general.users.colName')}</TableHead>
              <TableHead>{t('settingsPages.general.users.colEmail')}</TableHead>
              <TableHead>{t('settingsPages.general.users.colRole')}</TableHead>
              <TableHead>{t('settingsPages.general.users.colCloseRights')}</TableHead>
              <TableHead>{t('settingsPages.general.users.colStatus')}</TableHead>
              <TableHead>{t('settingsPages.general.users.colAdded')}</TableHead>
              <TableHead className="w-[150px] text-end">
                {t('settingsPages.general.users.colAction')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((m) => {
              const self = m.userId === me.data?.userId;
              const locked = m.role === 'outss_admin' && !isAdmin;
              return (
                <TableRow key={m.userId}>
                  <TableCell className="font-medium">
                    {m.name}
                    {self ? (
                      <Badge variant="secondary" className="ms-2">
                        {t('settingsPages.general.users.you')}
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>
                    {isRoleKey(m.role) ? t(`settingsPages.general.users.role.${m.role}`) : m.role}
                  </TableCell>
                  <TableCell>
                    {STAFF.has(m.role) ? (m.closeRights ? t('common.yes') : t('common.no')) : '—'}
                  </TableCell>
                  <TableCell>
                    {m.hasSignedIn ? (
                      <Badge variant="outline">{t('settingsPages.general.users.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('settingsPages.general.users.invited')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(m.createdAt)}</TableCell>
                  <TableCell className="text-end">
                    {canEdit && !locked ? (
                      <div className="inline-flex">
                        <Button
                          size="sm"
                          variant="link"
                          className="px-2"
                          onClick={() => setEditing({ open: true, member: m })}
                        >
                          {t('common.edit')}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label={t('settingsPages.general.users.moreActionsFor', {
                                name: m.name,
                              })}
                            >
                              <ChevronDown className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={self}
                              onSelect={() => setRemoving(m)}
                              title={
                                self ? t('settingsPages.general.users.cannotRemoveSelf') : undefined
                              }
                            >
                              {t('settingsPages.general.users.removeUser')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );

  return (
    <>
      <PageHeader
        title={t('settingsPages.general.users.title')}
        description={t('settingsPages.general.users.description')}
        actions={
          canEdit ? (
            <Button onClick={() => setEditing({ open: true, member: null })}>
              {t('settingsPages.general.users.addUser')}
            </Button>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : list.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : list.isError ? (
        <EmptyState title={t('settingsPages.general.users.loadError')} hint={list.error.message} />
      ) : (
        <div className="flex flex-col gap-4">
          {table(clients, t('settingsPages.general.users.clientUsers'))}
          {table(staff, t('settingsPages.general.users.accountingStaff'))}
          <p className="text-xs text-muted-foreground">
            {t('settingsPages.general.users.invitedNote')}
          </p>
        </div>
      )}
      <UserDrawer
        open={editing.open}
        member={editing.member}
        isAdmin={isAdmin}
        onOpenChange={(open) => setEditing((e) => ({ ...e, open }))}
      />
      <RemoveDialog member={removing} onClose={() => setRemoving(null)} />
    </>
  );
}

function UserDrawer({
  open,
  member,
  isAdmin,
  onOpenChange,
}: {
  open: boolean;
  member: Membership | null;
  isAdmin: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    email: '',
    name: '',
    role: 'client_viewer',
    closeRights: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      member
        ? {
            email: member.email,
            name: member.name,
            role: member.role,
            closeRights: member.closeRights,
          }
        : { email: '', name: '', role: 'client_viewer', closeRights: false },
    );
  }, [open, member]);

  const save = useMutation({
    mutationFn: () =>
      api<Membership[]>(`/orgs/${orgId}/memberships`, {
        method: 'PUT',
        body: JSON.stringify({
          email: form.email.trim(),
          ...(form.name.trim() ? { name: form.name.trim() } : {}),
          role: form.role,
          closeRights: STAFF.has(form.role) ? form.closeRights : false,
        }),
      }),
    onSuccess: (rows) => {
      qc.setQueryData(['memberships', orgId], rows);
      void qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
      toast(
        member
          ? t('settingsPages.general.users.updated', { name: form.name || ltr(form.email) })
          : t('settingsPages.general.users.invitedToast', { email: ltr(form.email) }),
      );
      onOpenChange(false);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
      next['email'] = t('settingsPages.general.users.invalidEmail');
    setErrors(next);
    if (!Object.keys(next).length) save.mutate();
  }

  // zod's email check is stricter than the one above; show its message on the field
  const server = serverFieldErrors(save.error);
  const roles = [
    'client_owner',
    'client_viewer',
    'outss_accountant',
    ...(isAdmin ? ['outss_admin'] : []),
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <form noValidate onSubmit={submit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>
              {member
                ? t('settingsPages.general.users.editUser')
                : t('settingsPages.general.users.addUser')}
            </SheetTitle>
            <SheetDescription>
              {member ? member.email : t('settingsPages.general.users.inviteHint')}
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <ErrorBanner>{save.error?.message}</ErrorBanner>
            <Field
              label={t('settingsPages.general.users.colEmail')}
              error={errors['email'] ?? server['email']}
            >
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={form.email}
                  disabled={Boolean(member)}
                  aria-invalid={Boolean(errors['email'] ?? server['email'])}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  autoFocus={!member}
                />
              )}
            </Field>
            <Field
              label={t('settingsPages.general.users.colName')}
              hint={member ? undefined : t('settingsPages.general.users.nameHint')}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.name}
                  maxLength={200}
                  disabled={Boolean(member)}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </Field>
            <Field
              label={t('settingsPages.general.users.colRole')}
              hint={
                isRoleKey(form.role)
                  ? t(`settingsPages.general.users.roleHelp.${form.role}`)
                  : undefined
              }
              error={server['role']}
            >
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                >
                  {roles.map((r) => (
                    <option key={r} value={r}>
                      {isRoleKey(r) ? t(`settingsPages.general.users.role.${r}`) : ROLE_LABELS[r]}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            {STAFF.has(form.role) ? (
              <Checkbox
                label={t('settingsPages.general.users.closeRightsLabel')}
                checked={form.closeRights}
                onChange={(e) => setForm((f) => ({ ...f, closeRights: e.target.checked }))}
              />
            ) : null}
          </div>
          <SheetFooter className="flex-row justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending
                ? t('common.saving')
                : member
                  ? t('common.save')
                  : t('settingsPages.general.users.sendInvite')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function RemoveDialog({ member, onClose }: { member: Membership | null; onClose: () => void }) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (x: Membership) =>
      api<Membership[]>(`/orgs/${orgId}/memberships/${x.userId}`, { method: 'DELETE' }),
    onSuccess: (rows, x) => {
      qc.setQueryData(['memberships', orgId], rows);
      void qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
      toast(t('settingsPages.general.users.removedToast', { name: x.name }));
      onClose();
    },
  });
  return (
    <Dialog
      open={Boolean(member)}
      onOpenChange={(open) => {
        if (!open) {
          m.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('settingsPages.general.users.removeTitle', { name: member?.name ?? '' })}
          </DialogTitle>
          <DialogDescription>
            {t('settingsPages.general.users.removeDescription')}
          </DialogDescription>
        </DialogHeader>
        {m.error ? (
          <p role="alert" className="text-sm text-destructive">
            {m.error.message}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={m.isPending}
            onClick={() => member && m.mutate(member)}
          >
            {t('settingsPages.general.users.removeUser')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
